import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableRow, TableHeader, TableHead } from "../ui/table";
import { formatCurrency } from "../../lib/helpers";
import { RentCastUnlockCard, DataSourcesBadge } from "./RentTransparency";

const LearnLink = ({ to, children }) => (
  <Link to={to} className="inline-flex items-center gap-1 text-[10px] text-emerald-500/80 hover:text-emerald-500 transition-colors" data-testid={`learn-link-${to.split('/').pop()}`}>
    <Info className="w-3 h-3" />{children}
  </Link>
);

export const ExpensesSection = ({ analysis, property, params, onParamsChange, isPro, isElite, isAuthed, onUpgrade }) => {
  const est = analysis.utility_estimates || {};
  const hasEstimates = est.electricity > 0;
  const allUtilitiesZero = !params?.electricity && !params?.gas && !params?.water_sewer && !params?.garbage && !params?.capex_percent;

  const handleAutoFill = () => {
    if (onParamsChange && est) {
      onParamsChange({
        ...params,
        capex_percent: est.capex_percent || 5,
        electricity: est.electricity || 0,
        gas: est.gas || 0,
        water_sewer: est.water_sewer || 0,
        garbage: est.garbage || 0,
      });
    }
  };

  // BiggerPockets-style calculation for comparison
  const bp = (() => {
    const rent = analysis.estimated_monthly_rent || 0;
    const price = analysis.purchase_price || 0;
    const vacancy = rent * 0.05; // BP default 5%
    const capex = rent * 0.05; // BP default 5%
    const repairs = rent * 0.05; // BP default 5%
    const mgmt = rent * 0.10; // BP default 10%
    const taxes = analysis.property_taxes_monthly || 0;
    const ins = analysis.insurance_monthly || 0;
    const mortgage = analysis.monthly_mortgage || 0;
    const opex = vacancy + capex + repairs + mgmt + taxes + ins;
    const noi = rent - opex;
    const cashFlow = noi - mortgage;
    return { vacancy, capex, repairs, mgmt, opex, noi, cashFlow, rent, price };
  })();

  return (
  <div className="space-y-4" data-testid="expenses-section">
    {/* Adjustable Expense Inputs */}
    {params && onParamsChange && (
      <div className="p-4 rounded-lg bg-secondary/20 border border-border">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Customize Expenses</p>
          {hasEstimates && allUtilitiesZero && (
            <Button variant="outline" size="sm" onClick={handleAutoFill} className="text-[10px] h-6 px-2 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10" data-testid="auto-fill-expenses-btn">
              Auto-fill estimates ({est.state})
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase">CapEx (% income)</Label>
            <Input type="number" value={params.capex_percent || 0} onChange={(e) => onParamsChange({ ...params, capex_percent: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} max={50} />
            {hasEstimates && !params.capex_percent && <p className="text-[9px] text-muted-foreground mt-0.5">Typical: 5%</p>}
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase">Electricity $/mo</Label>
            <Input type="number" value={params.electricity || 0} onChange={(e) => onParamsChange({ ...params, electricity: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} />
            {hasEstimates && !params.electricity && <p className="text-[9px] text-muted-foreground mt-0.5">Est: ${est.electricity}/mo</p>}
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase">Gas $/mo</Label>
            <Input type="number" value={params.gas || 0} onChange={(e) => onParamsChange({ ...params, gas: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} />
            {hasEstimates && !params.gas && <p className="text-[9px] text-muted-foreground mt-0.5">Est: ${est.gas}/mo</p>}
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase">Water & Sewer $/mo</Label>
            <Input type="number" value={params.water_sewer || 0} onChange={(e) => onParamsChange({ ...params, water_sewer: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} />
            {hasEstimates && !params.water_sewer && <p className="text-[9px] text-muted-foreground mt-0.5">Est: ${est.water_sewer}/mo</p>}
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase">Garbage $/mo</Label>
            <Input type="number" value={params.garbage || 0} onChange={(e) => onParamsChange({ ...params, garbage: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} />
            {hasEstimates && !params.garbage && <p className="text-[9px] text-muted-foreground mt-0.5">Est: ${est.garbage}/mo</p>}
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase">Other $/mo</Label>
            <Input type="number" value={params.other_expenses || 0} onChange={(e) => onParamsChange({ ...params, other_expenses: parseFloat(e.target.value) || 0 })} className="font-mono mt-1 h-8 text-sm" min={0} />
          </div>
        </div>
      </div>
    )}
    {/* RentCast-verified unlock CTA — only shows if rent isn't already rentcast-sourced and confidence isn't HIGH */}
    <RentCastUnlockCard
      property={property}
      analysis={analysis}
      isPro={isPro}
      isElite={isElite}
      isAuthed={isAuthed}
      onUpgrade={onUpgrade}
    />
    {/* Monthly Income & Expenses Table */}
    <Card className="bg-secondary/30 border-border">
      <CardContent className="p-0">
        <Table>
          <TableBody>
            {property.units > 1 && (
              <TableRow className="bg-emerald-500/5"><TableCell className="text-emerald-500 text-sm">Rent Per Unit</TableCell><TableCell className="font-mono text-right text-emerald-500 text-sm">{formatCurrency(analysis.rent_per_unit)}</TableCell></TableRow>
            )}
            <TableRow className="bg-emerald-500/5">
              <TableCell className="font-semibold text-emerald-500 text-sm">
                <div className="flex items-center gap-2">
                  {property.units > 1 ? `Gross Monthly Rent (${property.units} units)` : 'Gross Monthly Rent'}
                  {analysis.data_sources?.rent && <DataSourcesBadge source={analysis.data_sources.rent} />}
                </div>
              </TableCell>
              <TableCell className="font-mono text-right text-emerald-500 text-sm">{formatCurrency(analysis.estimated_monthly_rent)}</TableCell>
            </TableRow>
            <TableRow><TableCell className="text-muted-foreground text-sm">Vacancy ({analysis.zip_data?.vacancy_rate || 6}%)</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.vacancy_amount)}</TableCell></TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground text-sm">
                <div className="flex items-center gap-2">
                  Property Taxes
                  {analysis.data_sources?.taxes && <DataSourcesBadge source={analysis.data_sources.taxes} />}
                </div>
              </TableCell>
              <TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.property_taxes_monthly)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground text-sm">
                <div className="flex items-center gap-2">
                  Insurance
                  {analysis.data_sources?.insurance && <DataSourcesBadge source={analysis.data_sources.insurance} />}
                </div>
              </TableCell>
              <TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.insurance_monthly)}</TableCell>
            </TableRow>
            <TableRow><TableCell className="text-muted-foreground text-sm">Management ({analysis.zip_data?.management_fee_rate || 8}%)</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.management_fees_monthly)}</TableCell></TableRow>
            <TableRow><TableCell className="text-muted-foreground text-sm">Maintenance</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.maintenance_monthly)}</TableCell></TableRow>
            {analysis.capex_monthly > 0 && (
              <TableRow><TableCell className="text-muted-foreground text-sm">Capital Expenditures</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.capex_monthly)}</TableCell></TableRow>
            )}
            {analysis.hoa_monthly > 0 && (
              <TableRow><TableCell className="text-muted-foreground text-sm">HOA Fees</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.hoa_monthly)}</TableCell></TableRow>
            )}
            {(analysis.electricity_monthly > 0 || analysis.gas_monthly > 0 || analysis.water_sewer_monthly > 0 || analysis.garbage_monthly > 0) && (
              <>
                {analysis.electricity_monthly > 0 && <TableRow><TableCell className="text-muted-foreground text-sm pl-6">Electricity</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.electricity_monthly)}</TableCell></TableRow>}
                {analysis.gas_monthly > 0 && <TableRow><TableCell className="text-muted-foreground text-sm pl-6">Gas</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.gas_monthly)}</TableCell></TableRow>}
                {analysis.water_sewer_monthly > 0 && <TableRow><TableCell className="text-muted-foreground text-sm pl-6">Water & Sewer</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.water_sewer_monthly)}</TableCell></TableRow>}
                {analysis.garbage_monthly > 0 && <TableRow><TableCell className="text-muted-foreground text-sm pl-6">Garbage</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.garbage_monthly)}</TableCell></TableRow>}
              </>
            )}
            {analysis.other_monthly > 0 && (
              <TableRow><TableCell className="text-muted-foreground text-sm">Other Expenses</TableCell><TableCell className="font-mono text-right text-red-400 text-sm">-{formatCurrency(analysis.other_monthly)}</TableCell></TableRow>
            )}
            <TableRow className="bg-secondary/50">
              <TableCell className="font-semibold text-foreground text-sm">NOI (Monthly)</TableCell>
              <TableCell className={`font-mono text-right font-bold text-sm ${analysis.noi_monthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(analysis.noi_monthly)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground text-sm">
                {analysis.pmi_monthly > 0 ? "Mortgage (P&I)" : "Mortgage (P&I)"}
              </TableCell>
              <TableCell className="font-mono text-right text-red-400 text-sm">
                -{formatCurrency(analysis.monthly_pi ?? analysis.monthly_mortgage)}
              </TableCell>
            </TableRow>
            {analysis.pmi_monthly > 0 && (
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">
                  PMI <span className="text-[10px] text-muted-foreground/60">(required &lt; 20% down)</span>
                </TableCell>
                <TableCell className="font-mono text-right text-red-400 text-sm">
                  -{formatCurrency(analysis.pmi_monthly)}
                </TableCell>
              </TableRow>
            )}
            <TableRow className="bg-emerald-500/5 border-t-2 border-border">
              <TableCell className="font-bold text-foreground text-sm">Net Cash Flow (Monthly)</TableCell>
              <TableCell className={`font-mono text-right font-bold text-sm ${analysis.cash_flow_monthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(analysis.cash_flow_monthly)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    {/* Rent Estimates Comparison */}
    {/* 30-Year Repair Cost Projection */}
    {analysis.repair_projection && (
    <Card className="bg-secondary/30 border-border" data-testid="repair-projection">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold">Repair & Maintenance Costs</CardTitle>
            <CardDescription className="text-xs">
              {analysis.repair_projection.hold_years}-year projection based on property age, condition & size
            </CardDescription>
          </div>
          <div className="text-right">
            <p className="font-mono text-lg font-bold text-red-400">
              {formatCurrency(analysis.repair_projection.grand_total_30yr)}
            </p>
            <p className="text-[10px] text-muted-foreground">total over {analysis.repair_projection.hold_years}yr</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-secondary/50 border border-border text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Initial Repair</p>
            <p className="font-mono text-base font-bold text-foreground">{formatCurrency(analysis.repair_projection.initial_repair?.total || 0)}</p>
            <p className="text-[10px] text-muted-foreground">
              ${analysis.repair_projection.initial_repair?.cost_per_sqft || 0}/sqft
            </p>
          </div>
          <div className="p-3 rounded-lg bg-secondary/50 border border-border text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Avg Yearly</p>
            <p className="font-mono text-base font-bold text-amber-400">{formatCurrency(analysis.repair_projection.avg_yearly_cost)}</p>
            <p className="text-[10px] text-muted-foreground">
              {formatCurrency(analysis.repair_projection.avg_monthly_cost)}/mo
            </p>
          </div>
          <div className="p-3 rounded-lg bg-secondary/50 border border-border text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Annual Maint</p>
            <p className="font-mono text-base font-bold text-foreground">{formatCurrency(analysis.repair_projection.annual_maintenance?.annual || 0)}</p>
            <p className="text-[10px] text-muted-foreground">
              {analysis.repair_projection.annual_maintenance?.pct_of_value || 0}% of value
            </p>
          </div>
        </div>

        {/* Major Replacements */}
        {analysis.repair_projection.major_replacements?.items && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              Major Replacements ({analysis.repair_projection.hold_years}yr)
              <span className="ml-2 font-mono text-red-400">
                {formatCurrency(analysis.repair_projection.major_replacements.total_30yr)}
              </span>
            </p>
            <div className="space-y-1 max-h-[160px] overflow-y-auto">
              {analysis.repair_projection.major_replacements.items
                .filter(item => item.times_in_30yr > 0)
                .sort((a, b) => b.subtotal_30yr - a.subtotal_30yr)
                .map((item) => (
                <div key={item.name} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">{item.name}</span>
                    <span className="text-[10px] text-muted-foreground/60">
                      {item.times_in_30yr}x · every {item.lifespan_years}yr
                    </span>
                  </div>
                  <span className="font-mono text-red-400">{formatCurrency(item.subtotal_30yr)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <LearnLink to="/faq/how-realixdata-calculates-cash-flow">How we estimate costs</LearnLink>
      </CardContent>
    </Card>
    )}

    <Card className="bg-secondary/30 border-border" data-testid="rent-comparison">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm font-semibold">Rent Estimates Comparison</CardTitle>
        <CardDescription className="text-xs">{property.units > 1 ? `Per unit, ${property.units} units total` : 'From multiple data sources'}</CardDescription>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="grid grid-cols-2 gap-3">
          <div className={`p-3 rounded-lg border ${analysis.rent_source === 'zillow_zestimate' ? 'bg-blue-500/10 border-blue-500/40' : 'bg-secondary/50 border-border'}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-blue-400">Zillow Zestimate</span>
              {analysis.rent_source === 'zillow_zestimate' && <Badge className="bg-blue-500/20 text-blue-400 border-0 text-[9px] px-1.5 py-0">Used</Badge>}
            </div>
            {analysis.zillow_rent_zestimate ? (
              <p className="font-mono text-lg font-bold text-foreground">{formatCurrency(analysis.zillow_rent_zestimate)}<span className="text-[10px] text-muted-foreground font-normal">/mo</span></p>
            ) : <p className="text-xs text-muted-foreground italic">No data</p>}
          </div>
          <div className={`p-3 rounded-lg border ${analysis.rent_source === 'state_average' ? 'bg-amber-500/10 border-amber-500/40' : 'bg-secondary/50 border-border'}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-amber-500">State Avg</span>
              {analysis.rent_source === 'state_average' && <Badge className="bg-amber-500/20 text-amber-500 border-0 text-[9px] px-1.5 py-0">Used</Badge>}
            </div>
            <p className="font-mono text-lg font-bold text-foreground">{formatCurrency(analysis.state_avg_rent)}<span className="text-[10px] text-muted-foreground font-normal">/mo</span></p>
          </div>
        </div>
        <div className="mt-3 p-2.5 rounded-lg bg-secondary/50 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Rent used ({analysis.rent_source === 'zillow_zestimate' ? 'Zillow' : 'State Avg'})
            {property.units > 1 && ` x ${property.units} units`}
          </span>
          <span className="font-mono text-sm font-bold text-emerald-500">{formatCurrency(analysis.estimated_monthly_rent)}/mo</span>
        </div>
      </CardContent>
    </Card>

    {/* HOA, Flood Zone, Tax */}
    <div className="grid grid-cols-3 gap-3" data-testid="property-details-card">
      <div className={`p-3 rounded-lg border ${analysis.has_hoa ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">HOA</p>
        {analysis.has_hoa ? (
          <p className="font-mono text-base font-bold text-amber-500">{analysis.monthly_hoa_fee ? `$${analysis.monthly_hoa_fee.toLocaleString()}/mo` : 'Yes'}</p>
        ) : (
          <p className="font-mono text-base font-bold text-emerald-500">No HOA</p>
        )}
      </div>
      <div className={`p-3 rounded-lg border ${
        analysis.flood_risk_label === 'MINIMAL' || analysis.flood_risk_label === 'MINOR' ? 'bg-emerald-500/10 border-emerald-500/30' :
        analysis.flood_risk_label === 'MODERATE' ? 'bg-amber-500/10 border-amber-500/30' :
        analysis.flood_risk_label === 'MAJOR' || analysis.flood_risk_label === 'SEVERE' || analysis.flood_risk_label === 'EXTREME' ? 'bg-red-500/10 border-red-500/30' :
        'bg-secondary/50 border-border'
      }`}>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Flood Zone</p>
        {analysis.fema_flood_zone ? (
          <>
            <p className="font-mono text-base font-bold text-foreground">Zone {analysis.fema_flood_zone.split('_')[0]}</p>
            {analysis.flood_risk_label && (
              <p className={`text-[10px] font-medium ${
                analysis.flood_risk_label === 'MINIMAL' ? 'text-emerald-500' :
                analysis.flood_risk_label === 'MINOR' ? 'text-emerald-400' :
                analysis.flood_risk_label === 'MODERATE' ? 'text-amber-500' : 'text-red-500'
              }`}>{analysis.flood_risk_label} ({analysis.flood_risk_score}/10)</p>
            )}
          </>
        ) : <p className="text-xs text-muted-foreground italic">N/A</p>}
      </div>
      <div className="p-3 rounded-lg border bg-secondary/50 border-border">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Annual Tax</p>
        {analysis.tax_annual_amount ? (
          <>
            <p className="font-mono text-base font-bold text-foreground">${analysis.tax_annual_amount.toLocaleString()}/yr</p>
            <p className="text-[10px] text-muted-foreground">${Math.round(analysis.tax_annual_amount / 12).toLocaleString()}/mo</p>
          </>
        ) : <p className="text-xs text-muted-foreground italic">N/A</p>}
      </div>
    </div>

    {/* RealixData vs BiggerPockets Calculator Comparison */}
    <Card className="bg-secondary/30 border-border" data-testid="bp-comparison">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm font-semibold">RealixData vs BiggerPockets Calculator</CardTitle>
        <CardDescription className="text-xs">How our analysis compares using BP's standard assumptions (5% vacancy, 5% CapEx, 5% repairs, 10% management)</CardDescription>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Metric</TableHead>
              <TableHead className="text-xs text-right text-emerald-500">RealixData</TableHead>
              <TableHead className="text-xs text-right text-orange-400">BiggerPockets</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="text-sm text-muted-foreground">Gross Rent</TableCell>
              <TableCell className="font-mono text-right text-sm">{formatCurrency(analysis.estimated_monthly_rent)}</TableCell>
              <TableCell className="font-mono text-right text-sm">{formatCurrency(bp.rent)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-sm text-muted-foreground">Vacancy</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(analysis.vacancy_amount)}</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(bp.vacancy)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-sm text-muted-foreground">Taxes + Insurance</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency((analysis.property_taxes_monthly || 0) + (analysis.insurance_monthly || 0))}</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(bp.opex - bp.vacancy - bp.capex - bp.repairs - bp.mgmt)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-sm text-muted-foreground">CapEx</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">{analysis.capex_monthly > 0 ? `-${formatCurrency(analysis.capex_monthly)}` : <span className="text-muted-foreground/50">—</span>}</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(bp.capex)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-sm text-muted-foreground">Repairs/Maintenance</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(analysis.maintenance_monthly)}</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(bp.repairs)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-sm text-muted-foreground">Management</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(analysis.management_fees_monthly)}</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(bp.mgmt)}</TableCell>
            </TableRow>
            <TableRow className="bg-secondary/50">
              <TableCell className="font-semibold text-sm">NOI (Monthly)</TableCell>
              <TableCell className={`font-mono text-right font-bold text-sm ${analysis.noi_monthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(analysis.noi_monthly)}</TableCell>
              <TableCell className={`font-mono text-right font-bold text-sm ${bp.noi >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(bp.noi)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-sm text-muted-foreground">Mortgage (P&I)</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(analysis.monthly_mortgage)}</TableCell>
              <TableCell className="font-mono text-right text-sm text-red-400">-{formatCurrency(analysis.monthly_mortgage)}</TableCell>
            </TableRow>
            <TableRow className="bg-emerald-500/5 border-t-2">
              <TableCell className="font-bold text-sm">Cash Flow</TableCell>
              <TableCell className={`font-mono text-right font-bold text-sm ${analysis.cash_flow_monthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(analysis.cash_flow_monthly)}</TableCell>
              <TableCell className={`font-mono text-right font-bold text-sm ${bp.cashFlow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(bp.cashFlow)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-[9px] text-muted-foreground mt-2">BP uses flat 5% of rent for vacancy, CapEx, and repairs + 10% management. RealixData uses market-specific data with actual tax and insurance when available.</p>
      </CardContent>
    </Card>
  </div>
  );
};
